import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Coins,
  FileText,
  Factory,
  Sparkles,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import type {
  EstateServiceContent,
  EstateServicePair,
} from "@/content/industrial-estates/builder";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";
import { getEstateCoords } from "@/lib/geo-coords";

type NearbyEstate = {
  slug: string;
  nameTh: string;
  province: string;
  operator: string;
};
type EstateServiceData = {
  pair: EstateServicePair;
  content: EstateServiceContent;
  nearby: NearbyEstate[];
};

export const Route = createFileRoute("/industrial-estates/$estate/$service")({
  loader: async ({ params }): Promise<EstateServiceData> => {
    const [{ buildEstateServiceContent, resolveEstateServicePair }, { INDUSTRIAL_ESTATES }, { pickNearby }] =
      await Promise.all([
        import("@/content/industrial-estates/builder"),
        import("@/content/industrial-estates/estates"),
        import("@/lib/nearby-links"),
      ]);
    const pair = resolveEstateServicePair(params.estate, params.service);
    if (!pair) throw notFound();
    const nearby = pickNearby(
      INDUSTRIAL_ESTATES,
      pair.estate,
      (e) => e.region,
      4,
    ).map((n) => ({
      slug: n.slug,
      nameTh: n.nameTh,
      province: n.province,
      operator: n.operator,
    }));
    return { pair, content: buildEstateServiceContent(pair), nearby };
  },
  head: ({ params, loaderData }) => {
    const pair = loaderData?.pair;
    const site = getActiveSiteUrl();
    if (!pair || !loaderData) {
      return pageHeadForProvince({
        title: "ไม่พบหน้า",
        description: "ไม่พบหน้า",
        path: `/industrial-estates/${params.estate}/${params.service}`,
      });
    }
    const c = loaderData.content;
    const base = pageHeadForProvince({
      title: c.metaTitle,
      description: c.metaDesc,
      path: `/industrial-estates/${pair.estate.slug}/${pair.service.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "นิคมอุตสาหกรรม", url: `${site}/industrial-estates` },
              {
                name: pair.estate.nameTh,
                url: `${site}/industrial-estates/${pair.estate.slug}`,
              },
              {
                name: c.h1,
                url: `${site}/industrial-estates/${pair.estate.slug}/${pair.service.slug}`,
              },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(c.faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildHowToStepsSchema({ name: `ขั้นตอน: ${c.h1}`, description: c.metaDesc, path: `/industrial-estates/${params.estate}/${params.service}`, steps: c.process, inLanguage: "th" })) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/industrial-estates/${pair.estate.slug}/${pair.service.slug}#service`,
            name: c.h1,
            description: c.metaDesc,
            provider: { "@id": `${site}/#organization` },
            areaServed: {
              "@type": "Place",
              name: `${pair.estate.nameEn}, ${pair.estate.province}, Thailand`,
              ...(getEstateCoords(pair.estate.slug)
                ? {
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: getEstateCoords(pair.estate.slug)!.lat,
                      longitude: getEstateCoords(pair.estate.slug)!.lng,
                    },
                  }
                : {}),
            },
            audience: { "@type": "BusinessAudience", audienceType: "Industrial Estate B2B" },
            serviceType: pair.service.h1,
            url: `${site}/industrial-estates/${pair.estate.slug}/${pair.service.slug}`,
            ...(pair.service.pricing?.[0]?.price
              ? {
                  offers: {
                    "@type": "AggregateOffer",
                    priceCurrency: "THB",
                    lowPrice: pair.service.pricing[0].price.replace(/[^\d]/g, "") || undefined,
                    offerCount: pair.service.pricing.length,
                  },
                }
              : {}),
          }),
        },
        ...(getEstateCoords(pair.estate.slug)
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "LegalService",
                  "@id": `${site}/industrial-estates/${pair.estate.slug}/${pair.service.slug}#localbusiness`,
                  name: `${c.h1} — ${pair.estate.nameEn}`,
                  url: `${site}/industrial-estates/${pair.estate.slug}/${pair.service.slug}`,
                  telephone: COMPANY.phone,
                  priceRange: "฿฿",
                  areaServed: {
                    "@type": "Place",
                    name: `${pair.estate.nameEn}, ${pair.estate.province}`,
                  },
                  serviceArea: {
                    "@type": "GeoCircle",
                    geoMidpoint: {
                      "@type": "GeoCoordinates",
                      latitude: getEstateCoords(pair.estate.slug)!.lat,
                      longitude: getEstateCoords(pair.estate.slug)!.lng,
                    },
                    geoRadius: "10000",
                  },
                }),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/industrial-estates/${params.estate}/${params.service}`)) },
      ],
    };
  },
  component: EstateServicePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
        <Link to="/industrial-estates" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับ
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function EstateServicePage() {
  const { pair, content: c, nearby } = Route.useLoaderData() as EstateServiceData;
  const { estate, service } = pair;
  const serviceLabel = service.metaTitle.replace(/\s*\|\s*Thai Notary.*/i, "").trim();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/industrial-estates" className="hover:text-primary">นิคมอุตสาหกรรม</Link>
            <span className="mx-2">/</span>
            <Link
              to="/industrial-estates/$estate"
              params={{ estate: estate.slug }}
              className="hover:text-primary"
            >
              {estate.nameTh}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{service.h1.slice(0, 40)}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Sparkles className="h-3.5 w-3.5" />
              B2B · {estate.nameEn} · {estate.province}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{c.h1}</h1>
            <p
              id="ai-answer-tldr"
              className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed"
            >
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                สรุปสั้น
              </span>
              {c.tldr}
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            {c.quickFacts.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {f.label}
                </div>
                <div className="mt-1 text-sm font-bold">{f.value}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Factory className="h-5 w-5 text-gold" />
              ทำไม HR/Legal ของโรงงานใน {estate.nameTh} เลือกเรา
            </h2>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-muted-foreground">
              {c.whyHere.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Clock className="h-5 w-5 text-gold" /> ขั้นตอน B2B แบบ end-to-end
            </h2>
            <ol className="mt-6 space-y-4">
              {c.process.map((s) => (
                <li key={s.step} className="flex gap-4 rounded-xl border border-border bg-card p-5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {s.step}
                  </div>
                  <div>
                    <h3 className="font-bold">{s.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <FileText className="h-5 w-5 text-gold" /> เอกสารที่ต้องเตรียม
            </h2>
            <ul className="mt-4 grid gap-2 md:grid-cols-2">
              {c.documents.map((d, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Coins className="h-5 w-5 text-gold" /> ราคาอ้างอิง (ต่อเคส)
            </h2>
            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left">รายการ</th>
                    <th className="px-4 py-2 text-left">ราคา</th>
                  </tr>
                </thead>
                <tbody>
                  {service.pricing.slice(0, 6).map((p: { item: string; price: string }, i: number) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2">{p.item}</td>
                      <td className="px-4 py-2 font-bold">{p.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              ราคาแบบ batch สำหรับโรงงานในนิคม {estate.nameTh} สามารถลดได้ 10–20% เมื่อมีปริมาณรายเดือนคงที่ — ขอ MSA และใบเสนอราคาเป็น batch ได้
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย (ฝ่าย HR/Legal)</h2>
            <div className="mt-4 space-y-3">
              {c.faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer list-none font-bold">
                    <span className="mr-2 text-gold">Q.</span>
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {c.related.length > 0 && (
          <section className="border-b border-border bg-muted/20">
            <div className="mx-auto max-w-4xl px-4 py-12">
              <h2 className="text-xl font-bold">
                บริการ B2B อื่นใน {estate.nameTh}
              </h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {c.related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/industrial-estates/$estate/$service"
                    params={{ estate: estate.slug, service: r.slug }}
                    className="rounded-xl border border-border bg-card p-4 text-sm font-semibold transition hover:border-gold"
                  >
                    {r.label} →
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link
                  to="/industrial-estates/$estate"
                  params={{ estate: estate.slug }}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  ← กลับหน้าหลัก {estate.nameTh}
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="text-xl font-bold">
              {serviceLabel} ในนิคมใกล้เคียง {estate.nameTh}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              ทีม B2B ของเราวิ่งงานหลายนิคมในภูมิภาคเดียวกันต่อรอบ ช่วยลดค่าเดินเอกสารต่อโรงงาน ดูเพิ่มที่
              <Link to="/industrial-estates" className="mx-1 font-semibold text-primary hover:underline">
                นิคมอุตสาหกรรมทั้งหมด
              </Link>
              หรือ
              <Link to="/provinces" className="mx-1 font-semibold text-primary hover:underline">
                บริการรายจังหวัด 77 จังหวัด
              </Link>
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/industrial-estates/$estate/$service"
                  params={{ estate: n.slug, service: service.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition hover:border-gold"
                >
                  <span className="font-semibold">{serviceLabel} {n.nameTh}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {n.province} · {n.operator}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">{c.finalCta}</h2>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทรฝ่าย B2B {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener"
                className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold"
              >
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="business" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}